// Typed client for contracts/admin-api.md. Cookie auth (`lumen_admin`, httpOnly),
// so every request goes out with credentials. Responses are zod-parsed.

import { z } from "zod";
import { ADMIN_PREFIX, API_BASE } from "../env";
import {
  AdminResponse,
  ArenaDrops,
  ArenaMatch,
  ApproveRangeResponse,
  AuditPage,
  BadgeCatalogue,
  Config,
  ConfigPatchResponse,
  ConfigPutResponse,
  Dashboard,
  Divisions,
  DuelDetail,
  DuelList,
  ErrorBody,
  Integrity,
  Leaderboard,
  PackResponse,
  Paywall,
  PatchPuzzleResponse,
  PlayerDetail,
  Players,
  ProGrantResponse,
  PuzzleDetail,
  PuzzleList,
  ReportPatchResponse,
  Reports,
  RoomDetail,
  RoomList,
  Schedule,
  SwapResponse,
  TeamDetail,
  TeamList,
  TeamMutationResponse,
  TeamsCloseResult,
  Tracks,
  type DuelDetail as DuelDetailT,
  type DuelStatus,
  type JsonValue,
  type LeaderboardName,
  type PuzzleSort,
  type PuzzleStatus,
  type ReportPatch,
} from "./schemas";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export const isUnauthorized = (e: unknown) => e instanceof ApiError && e.status === 401;

type Query = Record<string, string | number | null | undefined>;

function qs(query?: Query): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

/** Exported for feature clients kept in their own modules (src/api/studio.ts). */
export async function request<S extends z.ZodType>(
  schema: S,
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  options: { query?: Query; body?: unknown; signal?: AbortSignal } = {},
): Promise<z.output<S>> {
  const url = `${API_BASE}${ADMIN_PREFIX}${path}${qs(options.query)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network", `Can't reach the admin API at ${API_BASE}. Is the backend running?`);
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      throw new ApiError(res.status, "bad_response", `${method} ${path} returned non-JSON (HTTP ${res.status}).`);
    }
  }

  if (!res.ok) {
    const err = ErrorBody.safeParse(body);
    throw new ApiError(
      res.status,
      err.success ? err.data.error.code : `http_${res.status}`,
      err.success ? err.data.error.message : `${method} ${path} failed with HTTP ${res.status}.`,
    );
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ApiError(
      res.status,
      "contract_mismatch",
      `${method} ${path} doesn't match contracts/admin-api.md:\n${z.prettifyError(parsed.error)}`,
    );
  }
  return parsed.data;
}

export interface PuzzleQuery {
  status?: PuzzleStatus | "";
  category?: string;
  type?: string;
  from?: string;
  to?: string;
  q?: string;
  cursor?: string | null;
  limit?: number;
  sort?: PuzzleSort;
}

const enc = encodeURIComponent;

/** A 404 that means the route itself is missing (Fastify's default body, or the contract shape saying so). */
const isMissingRoute = (e: unknown) => e instanceof ApiError && e.status === 404 && (e.code === "http_404" || /^Route [A-Z]+[: ]\S* not found/.test(e.message));

/** One status, every open one (queued, pending, choosing, active) or every closed one. */
export type DuelStatusFilter = DuelStatus | "open" | "closed" | null;
export type RoomStatusFilter = "open" | "closed" | null;

export const api = {
  // auth
  login: (email: string, password: string) =>
    request(AdminResponse, "POST", "/auth/login", { body: { email, password } }),
  logout: () => request(z.unknown(), "POST", "/auth/logout", { body: {} }),
  me: (signal?: AbortSignal) => request(AdminResponse, "GET", "/me", { signal }),

  // dashboard
  dashboard: (date?: string, signal?: AbortSignal) => request(Dashboard, "GET", "/dashboard", { query: { date }, signal }),

  // puzzle bank
  puzzles: (query: PuzzleQuery, signal?: AbortSignal) =>
    request(PuzzleList, "GET", "/puzzles", { query: { ...query, limit: query.limit ?? 50 }, signal }),
  puzzle: (id: string, signal?: AbortSignal) => request(PuzzleDetail, "GET", `/puzzles/${enc(id)}`, { signal }),
  setPuzzleStatus: (id: string, status: PuzzleStatus) =>
    request(PatchPuzzleResponse, "PATCH", `/puzzles/${enc(id)}`, { body: { status } }),

  // schedule
  schedule: (from: string, to: string, signal?: AbortSignal) =>
    request(Schedule, "GET", "/schedule", { query: { from, to }, signal }),
  swapDays: (dateA: string, dateB: string) => request(SwapResponse, "POST", "/schedule/swap", { body: { dateA, dateB } }),
  approveRange: (from: string, to: string) =>
    request(ApproveRangeResponse, "POST", "/schedule/approve", { body: { from, to } }),

  // levels
  tracks: (signal?: AbortSignal) => request(Tracks, "GET", "/levels/tracks", { signal }),
  pack: (track: string, index: number, signal?: AbortSignal) =>
    request(PackResponse, "GET", `/levels/packs/${enc(track)}/${index}`, { signal }),

  // players and integrity
  players: (q: string, signal?: AbortSignal) => request(Players, "GET", "/players", { query: { q }, signal }),
  player: (id: string, signal?: AbortSignal) => request(PlayerDetail, "GET", `/players/${enc(id)}`, { signal }),
  /** PATCH { handle: null } (editor+, audited as player.handle_reset); returns the player detail. */
  resetHandle: (id: string) => request(PlayerDetail, "PATCH", `/players/${enc(id)}`, { body: { handle: null } }),
  integrity: (date: string, signal?: AbortSignal) => request(Integrity, "GET", "/integrity", { query: { date }, signal }),

  // Phase 2 (contracts/phase2.md §12)
  /** POST { days, reason }: admin only, audited; stored as an entitlement with source=grant. */
  grantPro: (id: string, days: number, reason: string) =>
    request(ProGrantResponse, "POST", `/players/${enc(id)}/pro`, { body: { days, reason } }),
  /** DELETE the player's support grant: admin only, audited. */
  revokeProGrant: (id: string) => request(ProGrantResponse, "DELETE", `/players/${enc(id)}/pro/grant`),
  paywall: (from: string, to: string, signal?: AbortSignal) => request(Paywall, "GET", "/paywall", { query: { from, to }, signal }),
  badges: (signal?: AbortSignal) => request(BadgeCatalogue, "GET", "/badges", { signal }),
  /** The top 100 with user ids; `n` picks the puzzle for daily and hard (the server defaults to the latest). */
  leaderboard: (board: LeaderboardName, n?: number | null, signal?: AbortSignal) =>
    request(Leaderboard, "GET", `/leaderboards/${enc(board)}`, { query: { n }, signal }),
  /**
   * `open` asks for `?status=open`; `closed` (dismissed and actioned) asks for every report and keeps
   * the resolved ones, since the API filters by one status at a time.
   */
  reports: async (status: "open" | "closed", cursor?: string | null, signal?: AbortSignal) => {
    const page = await request(Reports, "GET", "/reports", { query: { status: status === "open" ? "open" : undefined, cursor }, signal });
    return status === "open" ? page : { ...page, items: page.items.filter((r) => r.status !== "open") };
  },
  /** Editor or above, audited. */
  updateReport: (id: string, patch: ReportPatch) => request(ReportPatchResponse, "PATCH", `/reports/${enc(id)}`, { body: patch }),

  // Phase 3: teams (contracts/phase3.md "Web and admin")
  teams: (q: string, status?: "active" | "dormant" | "disbanded" | null, signal?: AbortSignal) =>
    request(TeamList, "GET", "/teams", { query: { q, status }, signal }),
  team: (id: string, signal?: AbortSignal) => request(TeamDetail, "GET", `/teams/${enc(id)}`, { signal }),
  /** PATCH { name: null }: reset an offensive name to a generated one (editor+, audited). The panel refetches. */
  resetTeamName: (id: string) => request(TeamMutationResponse, "PATCH", `/teams/${enc(id)}`, { body: { name: null } }),
  /** DELETE: disband the team (admin only, audited). */
  disbandTeam: (id: string) => request(TeamMutationResponse, "DELETE", `/teams/${enc(id)}`),
  /** Division tables for an ISO week (the server defaults to the current one). */
  divisions: (week?: string | null, signal?: AbortSignal) => request(Divisions, "GET", "/divisions", { query: { week }, signal }),
  /**
   * POST /jobs/teams-close (admin only): `dryRun: true` plans the promotions, relegations and dormancies;
   * `dryRun: false` runs it. A token from the dry run is passed back when the server sent one.
   */
  teamsClose: (week: string, dryRun: boolean, token?: string | null) =>
    request(TeamsCloseResult, "POST", "/jobs/teams-close", { body: { week, dryRun, ...(token ? { token } : {}) } }),

  // Phase 4: arena, duels, rooms (contracts/phase4.md "Admin")
  /** Drops with T0 between two UTC dates, inclusive (plain dates: whole UTC days, Phase 4 follow-ups), newest first. */
  arenaDrops: (from: string, to: string, signal?: AbortSignal) => request(ArenaDrops, "GET", "/arena/drops", { query: { from, to }, signal }),
  /** One match's round-by-round replay data. */
  arenaMatch: (id: string, signal?: AbortSignal) => request(ArenaMatch, "GET", `/arena/matches/${enc(id)}`, { signal }),
  /** `q`: a duel or player id prefix, or a handle. */
  duels: (status: DuelStatusFilter, q?: string, signal?: AbortSignal) => request(DuelList, "GET", "/duels", { query: { status, q, limit: 200 }, signal }),
  /**
   * GET /duels/:id → `{ duel, series, history, flags }` (Phase 4 follow-ups); a backend with only the list
   * route answers from the list (searched by id) instead, with no series, history or flags.
   */
  duel: async (id: string, signal?: AbortSignal): Promise<DuelDetailT> => {
    try {
      return await request(DuelDetail, "GET", `/duels/${enc(id)}`, { signal });
    } catch (e) {
      if (!isMissingRoute(e)) throw e;
      const found = (await request(DuelList, "GET", "/duels", { query: { q: id, limit: 200 }, signal })).items.find((d) => d.id === id);
      if (!found) throw new ApiError(404, "not_found", `No duel ${id}`);
      return { duel: found, series: null, history: [], flags: [], full: false };
    }
  },
  /** Search by code, room id or player; `status` is also applied here, in case the API ignores it. */
  rooms: async (q: string, status: RoomStatusFilter, signal?: AbortSignal) => {
    const page = await request(RoomList, "GET", "/rooms", { query: { q, status, limit: 200 }, signal });
    return status ? { ...page, items: page.items.filter((r) => r.status === status) } : page;
  },
  /** GET /rooms/:code (a code or a room id; Phase 4 follow-ups); a backend with only the list route answers from the list instead. */
  room: async (id: string, signal?: AbortSignal) => {
    try {
      return await request(RoomDetail, "GET", `/rooms/${enc(id)}`, { signal });
    } catch (e) {
      if (!isMissingRoute(e)) throw e;
      const found = (await request(RoomList, "GET", "/rooms", { query: { q: id, limit: 200 }, signal })).items.find((r) => r.id === id || r.code === id);
      if (!found) throw new ApiError(404, "not_found", `No room ${id}`);
      // The list row carries a head count, not the members.
      return { room: found, members: found.members };
    }
  },

  // config
  config: (signal?: AbortSignal) => request(Config, "GET", "/config", { signal }),
  setConfig: (key: string, value: JsonValue) => request(ConfigPutResponse, "PUT", `/config/${enc(key)}`, { body: { value } }),
  /** PATCH /config: several keys at once, each validated, all or nothing (the paywall's cross-key rule sees them together). */
  patchConfig: (values: Record<string, JsonValue>) => request(ConfigPatchResponse, "PATCH", "/config", { body: { values } }),

  // audit
  audit: (cursor?: string | null, signal?: AbortSignal, limit = 50) =>
    request(AuditPage, "GET", "/audit", { query: { cursor, limit }, signal }),
};

export type Api = typeof api;
